import React, { createContext, useContext, useEffect, useMemo, useState } from "react";

const CartContext = createContext(null);

const STORAGE_KEY = "sabor-liquido-cart-v1";
const COUPON_KEY = "sabor-liquido-coupon-v1";

export const CartProvider = ({ children }) => {
    const [items, setItems] = useState(() => {
        try {
            const raw = localStorage.getItem(STORAGE_KEY);
            return raw ? JSON.parse(raw) : [];
        } catch {
            return [];
        }
    });
    const [coupon, setCouponState] = useState(() => {
        try {
            const raw = localStorage.getItem(COUPON_KEY);
            return raw ? JSON.parse(raw) : null;
        } catch {
            return null;
        }
    });
    const [open, setOpen] = useState(false);

    useEffect(() => {
        localStorage.setItem(STORAGE_KEY, JSON.stringify(items));
    }, [items]);

    useEffect(() => {
        if (coupon) {
            localStorage.setItem(COUPON_KEY, JSON.stringify(coupon));
        } else {
            localStorage.removeItem(COUPON_KEY);
        }
    }, [coupon]);

    const setCoupon = (c) => setCouponState(c);
    const clearCoupon = () => setCouponState(null);

    const addItem = (product, openDrawer = true) => {
        setItems((prev) => {
            const existing = prev.find((i) => i.id === product.id);
            if (existing) {
                return prev.map((i) =>
                    i.id === product.id ? { ...i, quantity: i.quantity + 1 } : i,
                );
            }
            return [...prev, { ...product, quantity: 1 }];
        });
        if (openDrawer) setOpen(true);
    };

    const updateQty = (id, qty) => {
        setItems((prev) =>
            prev
                .map((i) => (i.id === id ? { ...i, quantity: Math.max(0, qty) } : i))
                .filter((i) => i.quantity > 0),
        );
    };

    const removeItem = (id) => setItems((prev) => prev.filter((i) => i.id !== id));
    const clear = () => {
        setItems([]);
        setCouponState(null);
    };

    const totals = useMemo(() => {
        const subtotal = items.reduce((s, i) => s + i.price * i.quantity, 0);
        const count = items.reduce((s, i) => s + i.quantity, 0);
        return { subtotal: +subtotal.toFixed(2), count };
    }, [items]);

    return (
        <CartContext.Provider
            value={{
                items,
                addItem,
                updateQty,
                removeItem,
                clear,
                totals,
                open,
                setOpen,
                coupon,
                setCoupon,
                clearCoupon,
            }}
        >
            {children}
        </CartContext.Provider>
    );
};

export const useCart = () => {
    const ctx = useContext(CartContext);
    if (!ctx) throw new Error("useCart must be used within CartProvider");
    return ctx;
};
